import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Formik, Form, setNestedObjectValues } from "formik";
import { colors, shadow } from "./theme";
import { downloadInvoice } from "../data/mockBookings";
import StepIndicator from "./components/StepIndicator";
import NavBar from "./components/Navbar";
import ConfirmationScreen from "./components/Confirmation";
import StepVerify from "./steps/StepVerify";
import StepSchedule from "./steps/StepSchedule";
import StepServices from "./steps/StepServices";
import StepTerms from "./steps/StepTerms";
import StepReview from "./steps/StepReview";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import useRazorPay from "../../../hooks/useRazorPay";
import BookingService from "../../../services/BookingService";
import { bookingValidationSchema } from "../../../helpers/validationSchema";
import StepOtp from "./steps/StepOtp";
import {
  PageHeader,
  PageHeaderTop,
  PageHeaderBadge,
  PageHeaderTitle,
  PageHeaderDesc,
} from "./Styles";
import useSettings from "../../../hooks/useSettings";
import { states } from "../../../helpers/constants";
import ActionBar from "../../common/ActionBar";
import _ from "lodash";
import { navigate } from "@reach/router";

const STEP_LABELS = [
  "Verify",
  "Room & Schedule",
  "Services",
  "Review",
  "Terms & Pay",
];

const STEP_META = [
  {
    eyebrow: "Step 1 of 5 · Identity",
    title: "Verify Your Identity",
    desc: "Register or sign in to get started. We'll send an OTP to confirm your details before proceeding.",
  },
  {
    eyebrow: "Step 2 of 5 · Schedule",
    title: "Schedule Your Hearing",
    desc: "Pick a date, start time, and session duration, then choose from our available hearing rooms.",
  },
  {
    eyebrow: "Step 3 of 5 · Services",
    title: "Supplementary Services",
    desc: "Customise your hearing with catering, support staff, and accessibility options.",
  },
  {
    eyebrow: "Step 4 of 5 · Review",
    title: "Review & Party Details",
    desc: "Verify your booking summary and provide details for all parties and arbitrators.",
  },
  {
    eyebrow: "Step 5 of 5 · Terms & Pay",
    title: "Terms & Payment",
    desc: "Read and accept the terms, then complete your 50% advance to confirm the reservation.",
  },
];

const INITIAL_VALUES = {
  hearing_room: null,
  otp: "",
  hearing_date: "",
  start_time: "",
  session_duration: "halfday",
  numberOfAttendees: 4,
  catering: "none",
  mealBreakDown: {
    Vegetarian: 0,
    "Non-Vegetarian": 0,
    Jain: 0,
  },
  supportServices: {
    transcriptionServices: null,
    stenographerServices: null,
    caseManagerAssistance: null,
    tribunalSecretary: null,
    projectorServices: null,
    printingStationeryServices: null,
    videoConferencingServices: null,
    recordingSessionServices: null,
  },
  specialRequirements: [],
  termsAccepted: false,
  noOfArbitrators: 1,
  claimants_list: [
    {
      Name: "",
      Email: "",
      Phone: "",
      caseReference: "",
    },
  ],
  respondents_list: [
    {
      Name: "",
      Email: "",
      Phone: "",
      caseReference: "",
    },
  ],
  arbitrators_list: [
    {
      Name: "",
      Email: "",
      Phone: "",
      caseReference: "",
    },
  ],
  typeOfSelect: "register",
  ownerId: "",
  name: "",
  email: "",
  phone: "",
  houseNumber: "",
  locality: "",
  city: "",
  state: "TN",
  kind: "individual",
  documentNumber: "",
  password: "",
  otpService: "mobile",
  room_charge: 0,
  service_charge: 0,
  sub_total: "",
  gst_amount: "",
  total_amount: "",
  modeOfPayment: "cash",
  owner_details: {},
  cateringServices: {
    teaCoffeeRefreshments: null,
    meals: null,
  },
};

const canProceed = (step, values) => {
  switch (step) {
    case 0:
      return !!(values.name && values.email && values.phone);
    case 1:
      return !!(
        values.hearing_date &&
        values.start_time &&
        values.session_duration &&
        values.hearing_room
      );
    case 2:
      return true;
    case 3: {
      const isValidArray = (arr) =>
        Array.isArray(arr) &&
        arr.length > 0 &&
        arr.every(
          (item) =>
            item.Name?.trim() && item.Email?.trim() && item.Phone?.trim(),
        );
      return (
        isValidArray(values.claimants_list) &&
        isValidArray(values.respondents_list) &&
        isValidArray(values.arbitrators_list)
      );
    }
    case 4:
      return values.termsAccepted;
    default:
      return true;
  }
};

export default function Index() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [otp, setOtp] = useState({
    state: false,
  });
  const openRazorPay = useRazorPay();
  const { setting } = useSettings();
  const [referenceNumber, setReferenceNumber] = useState();

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "booking_dashboard":
        navigate("/dashboard/case-manager/booking-dashboard");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, otp.state, done]);

  const handleReset = () => {
    setStep(0);
    setDone(false);
    setOtp({ state: false });
  };

  const currentMeta = STEP_META[step] || STEP_META[0];

  async function capturePayment(rzp_res) {
    document.body.style.overflow = null;
    try {
      setLoader({ state: true, message: "Capturing Payment..." });
      const capture_res = await BookingService.captureRoomPayment(rzp_res);
      if (capture_res) {
        enqueueSnackbar(capture_res.message, {
          variant: "success",
        });
        if (capture_res?.invoice_url) {
          downloadInvoice(capture_res.invoice_url);
        }
        setDone(true);
        setReferenceNumber(capture_res?.booking?.booking_reference);
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), {
        variant: "error",
      });
      setDone(false);
    } finally {
      setLoader({ state: false });
      setLoading(false);
    }
  }

  /*

  const handleRazorpayDismiss = () => {
    document.body.style.overflow = null;
    setLoader({ state: false });
    setLoading(false);
    enqueueSnackbar(
      "Payment cancelled. Your booking details are still available.",
      {
        variant: "info",
      },
    );
  };

  */

  const launchRazorpay = (res) => {
    if (!res?.razorpayOrderId) {
      enqueueSnackbar("Unable to create Razorpay order.", {
        variant: "error",
      });
      setLoading(false);
      return;
    }
    openRazorPay({
      order_id: res.razorpayOrderId,
      handler: async (rzp_res) => {
        await capturePayment(rzp_res);
      },
      /*
      modal: {
        ondismiss: handleRazorpayDismiss,
      },
      */

      modal: {
        ondismiss: async () => {
          document.body.style.overflow = null;
          setLoader({ state: false });
          setLoading(false);
          try {
            await BookingService.quitPaymentService({
              booking_id: res?.booking?.id,
            });
            enqueueSnackbar("Payment cancelled.", {
              variant: "info",
            });
          } catch (error) {
            enqueueSnackbar(
              getErrorMessage(error) || "Unable to cancel payment.",
              {
                variant: "error",
              },
            );
          }
        },
      },
    });
  };

  const removeNulls = (obj) =>
    Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== null));

  const formatServiceCharges = (
    servicesObj = {},
    sessionDuration = "halfday",
  ) => {
    const durationHours = sessionDuration === "fullday" ? 6 : 3;
    return Object.values(servicesObj)
      .filter((service) => {
        return service && (service.active || service.type || service.pricing);
      })
      .map((service) => {
        let price = 0;

        if (service.type === "ON_REQUEST") {
          price = "ON_REQUEST";
        } else if (service.pricing?.offerPrice) {
          price = service.pricing.perHour
            ? Number(service.pricing.offerPrice) * durationHours
            : Number(service.pricing.offerPrice);
        } else if (service.pricing?.flat) {
          price = Number(service.pricing.flat);
        } else if (service.pricing?.perHour) {
          price = Number(service.pricing.perHour) * durationHours;
        }

        return {
          name: service.serviceName,
          price,
          hsnCode: service.hsnCode,
        };
      })
      .filter((s) => s.price !== 0);
  };

  /*
   * Main Form Submit
   */
  const onFormSubmit = async (values, resetForm) => {
    try {
      setLoader({ state: true, message: "Submitting..." });
      const mergedServices = {
        ...removeNulls(values.supportServices),
        ...removeNulls(values.cateringServices),
      };
      const payload = {
        owner_id: values?.ownerId,
        owner_details: {
          name: values?.name,
          email: values?.email,
          mobile: values?.phone,
          address: `${values.houseNumber}, ${values.locality}, ${values.city}`,
          state:
            states.find((item) => item.value === values.state)?.label || "",
          kind: values?.kind,
          documentNumber: values?.documentNumber,
        },
        hearing_room: values?.hearing_room?.roomName,
        hearing_date: values?.hearing_date,
        start_time: values?.start_time?.slice(0, 5),
        session_duration: values?.session_duration,
        room_charge: values?.room_charge,
        service_charge: formatServiceCharges(
          mergedServices,
          values?.session_duration,
        ),
        sub_total: values?.sub_total,
        gst_amount: values?.gst_amount,
        total_amount: values?.total_amount,
        claimants_list: values?.claimants_list,
        respondents_list: values?.respondents_list,
        arbitrators_list: values?.arbitrators_list,
        no_of_attendees:
          (values?.claimants_list?.length || 0) +
          (values?.respondents_list?.length || 0),
        invoiceType: "booking",
        typeOfPayment:
          values.modeOfPayment === "razorpayLink" ? "online" : "offline",
        modeOfPayment: values.modeOfPayment,
      };
      const res = await BookingService.hearingBooking(payload);
      if (!res?.booking) {
        enqueueSnackbar(res?.message || "Unable to create booking.", {
          variant: "error",
        });
        return;
      }
      if (values.modeOfPayment === "razorpayLink") {
        launchRazorpay(res);
        return;
      }
      enqueueSnackbar(res?.message, {
        variant: "success",
      });
      if (res?.invoice_url) {
        downloadInvoice(res.invoice_url);
      }
      setDone(true);
      setReferenceNumber(res?.booking_reference);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setLoading(false);
    }
  };

  const STEP_FIELDS = {
    0: otp.state
      ? ["otp"]
      : ["name", "email", "phone", "houseNumber", "locality", "city", "state"],

    1: ["hearing_date", "start_time", "session_duration", "hearing_room"],

    2: [],

    3: [
      "claimants_list",
      "respondents_list",
      "arbitrators_list",
      "documentNumber",
      "houseNumber",
      "locality",
      "city",
      "state",
    ],

    4: ["termsAccepted"],
  };

  if (done) {
    return (
      <Page>
        <ConfirmScrollWrap>
          <ConfirmationScreen
            onReset={handleReset}
            {...{
              referenceNumber,
            }}
          />
        </ConfirmScrollWrap>
      </Page>
    );
  }

  const getStepErrors = (errors, stepFields) =>
    Object.keys(errors).filter((key) =>
      stepFields.some((field) => key.startsWith(field)),
    );

  const sendOtp = async (values, setFieldValue) => {
    try {
      setLoader({ state: true, message: "otp sent...." });
      if (values?.typeOfSelect === "register") {
        const response = await BookingService.validateMobile({
          mobileToken: values.otp,
          mobile: `+91${values.phone}`,
          houseNumber: values.houseNumber,
          locality: values.locality,
          city: values.city,
          state: values.state,
          typeofReg: "bookingRoom",
        });
        if (response) {
          enqueueSnackbar(response?.message, {
            variant: "success",
          });
          setFieldValue("ownerId", response?.ownerId);
          setStep(1);
        }
      } else {
        const otpSentTime = localStorage.getItem("otpSentTime");
        if (!otpSentTime || Date.now() - Number(otpSentTime) > 10 * 60 * 1000) {
          enqueueSnackbar("OTP has expired. Please click Resend OTP.", {
            variant: "error",
          });
          return;
        }
        const response = await BookingService.otpLogin({
          otp: values.otp,
          mobile: `+91${values.phone}`,
          typeofReg: "bookingRoom",
        });
        if (response.verified) {
          setFieldValue("ownerId", response?.ownerId);
          setFieldValue("name", response?.name);
          setFieldValue("phone", response?.mobile);
          setFieldValue("email", response?.email);
          setFieldValue("houseNumber", response?.houseNumber || "");
          setFieldValue("locality", response?.locality || "");
          setFieldValue("city", response?.city || "");
          setFieldValue("state", response?.state || "");
          // setFieldValue("typeOfSelect", "register");
          enqueueSnackbar(response?.message, {
            variant: "success",
          });
          setStep(1);
          localStorage.removeItem("otpSentTime");
        } else {
          enqueueSnackbar(response?.message, {
            variant: "error",
          });
        }
      }
    } catch (err) {
      setOtp({
        state: true,
      });
      enqueueSnackbar(err?.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  const registerUser = async (values, setFieldValue) => {
    try {
      setLoader({ state: true, message: "otp sent...." });
      if (values?.typeOfSelect === "register") {
        const response = await BookingService.verifications({
          name: values?.name,
          email: values?.email,
          mobile: `+91${values.phone}`,
          password: "JustAct@123",
          typeofReg: "bookingRoom",
        });
        if (response) {
          enqueueSnackbar(response?.message, {
            variant: "success",
          });
          setOtp({ state: true });
          setFieldValue("otp", "");
        }
      } else {
        const response = await BookingService.login({
          mobile: `+91${values.phone}`,
          typeofReg: "bookingRoom",
        });
        if (response) {
          enqueueSnackbar(response?.message, {
            variant: "success",
          });
        }
        localStorage.setItem("otpSentTime", Date.now());
        setOtp({ state: true });
        setFieldValue("otp", "");
      }
    } catch (err) {
      enqueueSnackbar(err?.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  return (
    <>
      <ActionBar
        breadcrumbs={["Booking Dashboard", "Walk-in Booking"]}
        {...{
          onBreadcrumbClick,
        }}
      />
      <Page>
        <Layout>
          <TopBar>
            <StepIndicator steps={STEP_LABELS} current={step} />
          </TopBar>
          <Formik
            initialValues={INITIAL_VALUES}
            onSubmit={() => {}}
            validationSchema={bookingValidationSchema}
            enableReinitialize={false}
          >
            {({
              values,
              setFieldValue,
              errors,
              handleChange,
              handleBlur,
              touched,
              resetForm,
              validateForm,
              setTouched,
            }) => (
              <Form noValidate style={{ display: "contents" }}>
                <FormCard key={step}>
                  <PageHeader>
                    <PageHeaderTop>
                      <PageHeaderBadge>{currentMeta.eyebrow}</PageHeaderBadge>
                    </PageHeaderTop>
                    <PageHeaderTitle>{currentMeta.title}</PageHeaderTitle>
                    <PageHeaderDesc>{currentMeta.desc}</PageHeaderDesc>
                  </PageHeader>
                  <FormScrollArea>
                    {step === 0 &&
                      (otp?.state ? (
                        <StepOtp
                          {...{
                            handleBlur,
                            handleChange,
                            setFieldValue,
                            values,
                            errors,
                            touched,
                          }}
                        />
                      ) : (
                        <StepVerify
                          {...{
                            handleBlur,
                            handleChange,
                            setFieldValue,
                            values,
                            errors,
                          }}
                        />
                      ))}
                    {step === 1 && (
                      <StepSchedule
                        {...{
                          handleBlur,
                          handleChange,
                          setFieldValue,
                          values,
                          errors,
                          touched,
                        }}
                        setError={() => {}}
                        roomsPricingInfo={setting?.roomsPricingInfo}
                      />
                    )}
                    {step === 2 && (
                      <StepServices
                        {...{
                          handleBlur,
                          handleChange,
                          setFieldValue,
                          values,
                          errors,
                        }}
                        servicesPricingInfo={setting?.servicesPricingInfo}
                        cateringServicesInfo={setting?.cateringServicesInfo}
                      />
                    )}
                    {step === 3 && (
                      <StepReview
                        {...{
                          handleBlur,
                          handleChange,
                          setFieldValue,
                          values,
                          errors,
                        }}
                      />
                    )}
                    {step === 4 && (
                      <StepTerms
                        {...{
                          handleBlur,
                          handleChange,
                          setFieldValue,
                          values,
                          errors,
                        }}
                      />
                    )}
                  </FormScrollArea>
                  <NavBarWrap>
                    <NavBar
                      step={step}
                      totalSteps={STEP_LABELS.length}
                      canGoNext={canProceed(step, values)}
                      isLast={step === STEP_LABELS.length - 1}
                      loading={loading}
                      otpState={otp.state}
                      /*
                      onBack={() => {
                        if (step === 0 && otp.state) {
                          setOtp({ state: false });
                          return;
                        }
                        if (step > 0) {
                          setStep((s) => s - 1);
                        }
                      }}
                      */
                      onBack={() => {
                        if (step === 0 && otp.state) {
                          setOtp({ state: false });
                          return;
                        }
                        // if (step > 0) setStep((s) => s - 1);
                        if (step > 0) {
                          if (step === 1) {
                            setOtp({ state: false });
                            setFieldValue("otp", "");
                            setFieldValue("ownerId", "");

                            const rawPhone =
                              (typeof values.phone === "string" &&
                                values.phone) ||
                              "";
                            setFieldValue(
                              "phone",
                              rawPhone.replace(/^\+91/, ""),
                            );
                            try {
                              localStorage.removeItem("otpSentTime");
                            } catch (e) {}
                          }
                          setStep((s) => s - 1);
                        }
                      }}
                      onNext={async () => {
                        const errs = await validateForm();
                        const stepFields = STEP_FIELDS[step];
                        const stepErrors = getStepErrors(errs, stepFields);
                        const touchedFields = setNestedObjectValues(
                          stepFields.reduce((acc, key) => {
                            acc[key] = values[key];
                            return acc;
                          }, {}),
                          true,
                        );
                        setTouched(touchedFields);
                        if (stepErrors.length > 0) {
                          return;
                        }
                        if (step === 0 && !otp.state) {
                          await registerUser(values, setFieldValue);
                          return;
                        }
                        if (step === 0 && otp.state) {
                          if (!values.otp || values.otp.length !== 6) {
                            return;
                          }
                          setOtp({
                            state: false,
                          });
                          await sendOtp(values, setFieldValue);
                          return;
                        }
                        setStep((s) => s + 1);
                      }}
                      onSubmit={async () => {
                        if (!canProceed(step, values)) {
                          return;
                        }
                        if (loading) {
                          return;
                        }
                        setLoading(true);
                        await onFormSubmit(values, resetForm);
                      }}
                    />
                  </NavBarWrap>
                </FormCard>
              </Form>
            )}
          </Formik>
        </Layout>
      </Page>
    </>
  );
}

const Page = styled.div`
  background: ${colors.surface};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Layout = styled.div`
  width: 100%;
  max-width: 90%;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  @media (max-width: 480px) {
    padding: 0 8px;
  }
`;

const TopBar = styled.div`
  flex-shrink: 0;
`;

const FormCard = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${colors.white};
  border-radius: 0;
  box-shadow: ${shadow.lg};
  border: 1.5px solid ${colors.border};
  border-bottom: none;
  position: relative;
  overflow: hidden;

  @media (max-width: 480px) {
    border-radius: 0;
  }
`;

const FormScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px 28px 8px;

  scrollbar-width: thin;
  scrollbar-color: ${colors.border} transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.border};
    border-radius: 99px;
  }

  @media (max-width: 640px) {
    padding: 18px 16px 8px;
  }

  @media (max-width: 400px) {
    padding: 14px 12px 8px;
  }
`;

const NavBarWrap = styled.div`
  flex-shrink: 0;
  padding: 0 28px 16px;
  background: ${colors.white};

  @media (max-width: 640px) {
    padding: 0 16px 12px;
  }
`;

const ConfirmScrollWrap = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: env(safe-area-inset-bottom, 16px);
`;
